import { motion } from "framer-motion";
import { Check, ArrowRight } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, BookDemoButton, LinkButton } from "@/components/marketing/Primitives";
import { OpsDashboard, OpsCalendar, EngineerApp } from "@/components/marketing/ProductMockups";
import { featureSections } from "@/lib/content";

const MOCKS = [OpsDashboard, OpsCalendar, EngineerApp];

function FeatureBlock({ section, index }) {
  const reverse = index % 2 === 1;
  const Mock = MOCKS[index % MOCKS.length];
  return (
    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <span className="font-heading text-sm font-bold text-primary">{String(index + 1).padStart(2, "0")}</span>
        <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">{section.title}</h3>
        <p className="mt-4 leading-relaxed text-muted-foreground md:text-lg">{section.desc}</p>
        <ul className="mt-6 space-y-3">
          {section.benefits.map((b) => (
            <li key={b} className="flex items-start gap-3 text-white">
              <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check size={14} weight="bold" />
              </span>
              {b}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
        <motion.div
          whileHover={{ y: -6 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-card"
        >
          <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-2/3 -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" />
          <div className="flex items-center gap-1.5 border-b border-white/10 bg-black/40 px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="ml-2 text-xs text-muted-foreground">FlowCore — {section.title}</span>
          </div>
          <div className="aspect-[16/10] w-full"><Mock /></div>
        </motion.div>
      </Reveal>
    </div>
  );
}

export default function Features() {
  return (
    <>
      <SEO
        title="Features — FlowCore Field Service Platform"
        description="Explore FlowCore's features: CRM, scheduling, operations calendar, engineer app, jobs, assets, stock control, service plans, digital jobsheets, customer portal, reporting and AI."
      />
      <section className="relative overflow-hidden pt-36 pb-16">
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Features</Eyebrow>
            <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              One platform for every part of the job
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-muted-foreground md:text-lg">
              From the first enquiry to the final invoice, FlowCore connects your whole operation — removing admin, cutting errors and saving thousands in lost time and stock.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <BookDemoButton testid="features-book-demo" />
              <LinkButton to="/pricing" testid="features-pricing">See Pricing</LinkButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto flex max-w-7xl flex-col gap-24 px-5 sm:px-8">
          {featureSections.map((s, i) => (
            <FeatureBlock key={s.key} section={s} index={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 py-20 text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">See the full platform live</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Book a tailored walkthrough and we'll show you exactly how FlowCore fits your business.</p>
          <div className="mt-8 flex justify-center gap-4">
            <BookDemoButton testid="features-cta-demo" />
            <LinkButton to="/contact" testid="features-cta-contact">Contact Us <ArrowRight size={16} weight="bold" /></LinkButton>
          </div>
        </div>
      </section>
    </>
  );
}
